import { useEffect, useMemo, useState } from 'react';
import { CourseOption, ClassSection, Meeting } from '../../types/course.response';
import { parseTimeToMinutes } from '../../utils/timeUtils';
import { getCourseKey } from '../../utils/courseUtils';

interface WeeklyScheduleGridProps {
  semester: string;
  dateRange: [string, string];
  colorMap: Record<string, string>;
  currentCourses: CourseOption[];
  highlightedCourseKey?: string;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const HOUR_HEIGHT = 80;
const TIME_LABEL_WIDTH = 3; // 3rem = w-12

interface PositionedBlock {
  courseId: string;
  sectionId: string;
  dayIndex: number;
  startMinutes: number;
  endMinutes: number;
  meeting: Meeting;
  section: ClassSection;
  course: CourseOption;
  meetingIndex: number;
  columnIndex: number;
  totalColumns: number;
}

const timesOverlap = (
  start1: number, 
  end1: number, 
  start2: number, 
  end2: number
): boolean => {
  return start1 < end2 && start2 < end1;
};

const calculateOverlapLayout = (blocks: Omit<PositionedBlock, 'columnIndex' | 'totalColumns'>[]): PositionedBlock[] => {
  const blocksByDay: Record<number, typeof blocks> = {};
  blocks.forEach(block => {
    if (!blocksByDay[block.dayIndex]) {
      blocksByDay[block.dayIndex] = [];
    }
    blocksByDay[block.dayIndex].push(block);
  });

  const positionedBlocks: PositionedBlock[] = [];

  Object.entries(blocksByDay).forEach(([day, dayBlocks]) => {
    const sortedBlocks = [...dayBlocks].sort((a, b) => {
      if (a.startMinutes !== b.startMinutes) {
        return a.startMinutes - b.startMinutes;
      }
      return (b.endMinutes - b.startMinutes) - (a.endMinutes - a.startMinutes);
    });

    type BlockType = typeof sortedBlocks[0];
    const overlapGroups: BlockType[][] = [];
    const blockToGroupIndex = new Map<BlockType, number>();

    sortedBlocks.forEach(block => {
      const overlappingGroupIndices = new Set<number>();
      
      sortedBlocks.forEach((otherBlock, otherIndex) => {
        if (block === otherBlock) return;
        if (timesOverlap(block.startMinutes, block.endMinutes, otherBlock.startMinutes, otherBlock.endMinutes)) {
          const groupIndex = blockToGroupIndex.get(otherBlock);
          if (groupIndex !== undefined) {
            overlappingGroupIndices.add(groupIndex);
          }
        }
      });

      if (overlappingGroupIndices.size === 0) {
        const newGroupIndex = overlapGroups.length;
        overlapGroups.push([block]);
        blockToGroupIndex.set(block, newGroupIndex);
      } else {
        const groupIndices = Array.from(overlappingGroupIndices).sort((a, b) => b - a);
        const primaryGroupIndex = groupIndices[0];
        
        overlapGroups[primaryGroupIndex].push(block);
        blockToGroupIndex.set(block, primaryGroupIndex);
        
        for (let i = 1; i < groupIndices.length; i++) {
          const groupToMerge = overlapGroups[groupIndices[i]];
          groupToMerge.forEach(b => {
            overlapGroups[primaryGroupIndex].push(b);
            blockToGroupIndex.set(b, primaryGroupIndex);
          });
          overlapGroups[groupIndices[i]] = [];
        }
      }
    });

    const groupMaxColumns = overlapGroups.map(group => {
      if (group.length <= 1) return 1;
      
      const timePoints = new Set<number>();
      group.forEach(block => {
        timePoints.add(block.startMinutes);
        timePoints.add(block.endMinutes);
      });
      const sortedTimePoints = Array.from(timePoints).sort((a, b) => a - b);
      
      let maxOverlaps = 1;
      for (let i = 0; i < sortedTimePoints.length - 1; i++) {
        const time = sortedTimePoints[i];
        const overlapsAtTime = group.filter(block => 
          block.startMinutes <= time && time < block.endMinutes
        ).length;
        maxOverlaps = Math.max(maxOverlaps, overlapsAtTime);
      }
      
      return maxOverlaps;
    });

    sortedBlocks.forEach(block => {
      const groupIndex = blockToGroupIndex.get(block)!;
      const group = overlapGroups[groupIndex];
      const totalColumns = groupMaxColumns[groupIndex];
      
      const columns: Array<{ block: BlockType, endTime: number }> = [];
      
      let columnIndex = 0;
      const groupBlocksSorted = [...group].sort((a, b) => {
        if (a.startMinutes !== b.startMinutes) {
          return a.startMinutes - b.startMinutes;
        }
        return (b.endMinutes - b.startMinutes) - (a.endMinutes - a.startMinutes);
      });
      
      for (const groupBlock of groupBlocksSorted) {
        if (groupBlock === block) {
          let foundColumn = false;
          for (let i = 0; i < columns.length; i++) {
            if (columns[i].endTime <= block.startMinutes) {
              columnIndex = i;
              columns[i] = { block, endTime: block.endMinutes };
              foundColumn = true;
              break;
            }
          }
          if (!foundColumn) {
            columnIndex = columns.length;
            columns.push({ block, endTime: block.endMinutes });
          }
          break;
        } else {
          let foundColumn = false;
          for (let i = 0; i < columns.length; i++) {
            if (columns[i].endTime <= groupBlock.startMinutes) {
              columns[i] = { block: groupBlock, endTime: groupBlock.endMinutes };
              foundColumn = true;
              break;
            }
          }
          if (!foundColumn) {
            columns.push({ block: groupBlock, endTime: groupBlock.endMinutes });
          }
        }
      }

      positionedBlocks.push({
        ...block,
        columnIndex,
        totalColumns
      });
    });
  });

  return positionedBlocks;
};

export default function WeeklyScheduleGrid({ 
  semester, 
  colorMap, 
  dateRange, 
  currentCourses,
  highlightedCourseKey,
}: WeeklyScheduleGridProps) {
  const [scheduledCourses, setScheduledCourses] = useState<CourseOption[]>([]);
  useEffect(() => {
    setScheduledCourses(currentCourses);
  }, [currentCourses, semester]);

  const checkedCourses = useMemo(() => {
    return scheduledCourses.filter(course => course.userState.isScheduled);
  }, [scheduledCourses]);

  const totalCredits = useMemo(() => {
    return checkedCourses.reduce((sum, course) => {
      return sum + (course.userState.creditsReceived ?? 0);
    }, 0);
  }, [checkedCourses]);

  const sectionIsSelected = (section: ClassSection, scheduledCourse: CourseOption) => {
    if (!scheduledCourse.userState.sectionNumbers) {
      return false;
    }
    return scheduledCourse.userState.sectionNumbers.includes(section.sectionNumber);
  }
  
  const meetingInDateRange = (meeting: Meeting, dateRange: [string, string]) => {
    return true; // TODO: implement this
  }
  
  const { startHour, endHour, hours } = useMemo(() => {
    let minHour = 8;
    let maxHour = 21;
    
    for (const scheduledCourse of scheduledCourses) {
      for (const enrollGroup of scheduledCourse.enrollGroups) {
        if (enrollGroup.semester !== semester) continue;

        const sections = enrollGroup.classSections.filter(section => 
          sectionIsSelected(section, scheduledCourse)
        );
        
        for (const section of sections) {
          const meetingsToDisplay = section.meetings.filter(meeting => 
            meetingInDateRange(meeting, dateRange)
          );
          
          for (const meeting of meetingsToDisplay) {
            const startMinutes = parseTimeToMinutes(meeting.timeStart ?? "");
            const endMinutes = parseTimeToMinutes(meeting.timeEnd ?? "");

            if (startMinutes === undefined || endMinutes === undefined) {
              continue;
            }

            if (startMinutes < minHour * 60) {
              minHour = Math.floor(startMinutes / 60);
            }
            if (endMinutes > maxHour * 60) {
              maxHour = Math.ceil(endMinutes / 60);
            }
          }
        }
      }
    }
    
    return {
      startHour: minHour,
      endHour: maxHour,
      hours: Array.from({ length: maxHour - minHour + 1 }, (_, i) => i + minHour)
    };
  }, [scheduledCourses, semester, dateRange]);

  const parseDaysPattern = (pattern: string): number[] => {
    const dayMap: Record<string, number> = { 'M': 0, 'T': 1, 'W': 2, 'R': 3, 'F': 4 };
    const result = Array.from(pattern)
      .map(char => dayMap.hasOwnProperty(char) ? dayMap[char] : -1)
      .filter(idx => idx !== -1);
    return result;
  };
  
  const getScheduledCourseBlocks = () => {
    const allBlocks: Omit<PositionedBlock, 'columnIndex' | 'totalColumns'>[] = [];
    
    for (const scheduledCourse of scheduledCourses) {
      for (const enrollGroup of scheduledCourse.enrollGroups) {
        if (enrollGroup.semester !== semester) continue;

        const sections = enrollGroup.classSections.filter(section => 
          sectionIsSelected(section, scheduledCourse)
        );
        
        for (const section of sections) {
          const meetingsToDisplay = section.meetings.filter(meeting => 
            meetingInDateRange(meeting, dateRange)
          );
          
          for (let meetingIndex = 0; meetingIndex < meetingsToDisplay.length; meetingIndex++) {
            const meeting = meetingsToDisplay[meetingIndex];
            const days = parseDaysPattern(meeting.pattern ?? "");
              
            days.forEach(dayIndex => {
              const startMinutes = parseTimeToMinutes(meeting.timeStart ?? "");
              const endMinutes = parseTimeToMinutes(meeting.timeEnd ?? "");
              if (startMinutes === undefined || endMinutes === undefined) {
                return;
              }

              allBlocks.push({
                courseId: scheduledCourse.id,
                sectionId: `${section.sectionType}-${section.sectionNumber}`,
                dayIndex,
                startMinutes,
                endMinutes,
                meeting,
                section,
                course: scheduledCourse,
                meetingIndex,
              });
            });
          }
        }
      }
    }

    const positionedBlocks = calculateOverlapLayout(allBlocks);

    return positionedBlocks.map((block, index) => {
      const duration = block.endMinutes - block.startMinutes;
      const top = ((block.startMinutes - startHour * 60) / 60) * HOUR_HEIGHT;
      const height = Math.max((duration / 60) * HOUR_HEIGHT, 30);

      const dayColumnWidth = `(100% - ${TIME_LABEL_WIDTH}rem) / 5`;
      const blockWidth = `((${dayColumnWidth}) / ${block.totalColumns})`;
      const leftOffset = `calc(${TIME_LABEL_WIDTH}rem + (${dayColumnWidth} * ${block.dayIndex}) + (${blockWidth} * ${block.columnIndex}) + 2px)`;

      const courseKey = getCourseKey(block.course);
      const isHighlighted = highlightedCourseKey === courseKey;

      return (
        <div
          key={`${block.courseId}-${block.sectionId}-${block.dayIndex}-${block.meetingIndex}-${index}`}
          className={`absolute rounded-lg shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all duration-200 ${
            isHighlighted
              ? 'border-2 border-dashed border-white ring-2 ring-white/50 z-20 scale-[1.02]'
              : highlightedCourseKey
                ? 'border border-white/20 opacity-40'
                : 'border border-white/20'
          }`}
          style={{
            top: `${top}px`,
            height: `${height}px`,
            left: leftOffset,
            width: `calc(${blockWidth} - 4px)`,
            backgroundColor: colorMap[courseKey] || '#64748b',
          }}
        >
          <div className="p-2 h-full flex flex-col text-white">
            <div className="font-bold text-[.85rem] leading-tight">
              {block.course.courseInfo.subject} {block.course.courseInfo.number}
            </div>
            <div className="text-xs mt-1">
              {block.section.sectionType} {block.section.sectionNumber}
            </div>
            <div className="text-xs mt-auto">
              {block.meeting.timeStart} - {block.meeting.timeEnd}
            </div>
          </div>
        </div>
      );
    });
  };
  
  const courseBlocks = useMemo(() => getScheduledCourseBlocks(), [
    scheduledCourses, 
    colorMap, 
    semester, 
    dateRange, 
    startHour,
    highlightedCourseKey
  ]);
  
  const formatHour = (hour: number): string => {
    if (hour === 0) return '12am';
    if (hour === 12) return '12pm';
    if (hour < 12) return `${hour}am`;
    return `${hour - 12}pm`;
  };
  const totalHeight = (endHour - startHour + 1) * HOUR_HEIGHT;
  
  return (
    <div>
      <div className="flex items-center justify-center mb-4">
        <span className="text-sm">
          <span className="text-lg font-bold">{checkedCourses.length}</span> Courses{' '}
          <span className="text-lg font-bold">{totalCredits}</span> Credits
        </span>
      </div>
    <div className="mx-auto bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden h-[700px] flex flex-col w-full max-w-full">
      <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ scrollbarGutter: 'stable' }}>
        {/* Sticky Header */}
        <div className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex">
            <div className="w-12 flex-shrink-0 bg-gray-50 border-r border-gray-200"></div>
            <div className="flex-1 grid grid-cols-5">
              {DAYS.map((day, index) => (
                <div 
                  key={day} 
                  className={`text-center py-2 font-semibold text-gray-700 bg-gray-50 text-[16px] ${
                    index < 4 ? 'border-r border-gray-200' : ''
                  }`}
                >
                  {day}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="relative" style={{ height: `${totalHeight}px` }}>
          {/* Time Grid */}
          {hours.map((hour, i) => (
            <div 
              key={hour} 
              className="absolute w-full flex border-b border-gray-100" 
              style={{ top: `${i * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
            >
              {/* Time Label */}
              <div className="w-12 flex-shrink-0 flex items-start justify-end pr-2 pt-1 bg-gray-50/50 border-r border-gray-200">
                <span className="text-xs text-gray-700 font-medium">
                  {formatHour(hour)}
                </span>
              </div>
              
              {/* Day Columns */}
              <div className="flex-1 grid grid-cols-5 h-full">
                {DAYS.map((_, dayIndex) => (
                  <div 
                    key={dayIndex} 
                    className={`h-full ${dayIndex < 4 ? 'border-r border-gray-100' : ''} hover:bg-gray-50/30 transition-colors duration-150`}
                  >
                    {/* Half-hour line */}
                    <div className="absolute w-full border-b border-gray-50" style={{ top: `${HOUR_HEIGHT / 2}px` }}></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* Course Blocks */}
          <div className="relative z-10">
            {courseBlocks}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
